import React, { useState, useEffect, useCallback } from 'react';
import { X, Robot, ArrowRight, CaretDown, CaretUp, Spinner } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import ReactMarkdown from 'react-markdown';
import { getOeeDrilldown, oeeAiAssist, trackTelemetry } from '../api';

function fmtVnd(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n?.toLocaleString?.() ?? n;
}

export default function OeeDrilldownPanel({ metricKey, metricLabel, metricValue, onClose }) {
  const [drilldown, setDrilldown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAi, setShowAi] = useState(false);

  const fetchDrilldown = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOeeDrilldown(metricKey);
      setDrilldown(res.data);
      trackTelemetry({ event_type: 'feature_use', page: 'oee', feature: 'kpi_drilldown', detail: metricKey }).catch(() => {});
    } catch (err) {
      console.error('Drilldown fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [metricKey]);

  useEffect(() => { fetchDrilldown(); }, [fetchDrilldown]);

  const handleAiAssist = async () => {
    if (aiInsight) { setShowAi(!showAi); return; }
    setAiLoading(true);
    setShowAi(true);
    trackTelemetry({ event_type: 'feature_use', page: 'oee', feature: 'ai_assist', detail: metricKey }).catch(() => {});
    try {
      const res = await oeeAiAssist({
        metric_key: metricKey,
        metric_value: metricValue,
        metric_label: metricLabel,
        context: {
          line_breakdown: drilldown?.line_breakdown?.slice(0, 5),
          related_losses: drilldown?.related_losses?.map(l => l.category),
        },
      });
      setAiInsight(res.data.analysis);
    } catch (err) {
      setAiInsight('AI analysis unavailable. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="oee-drilldown-panel">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-background border-l border-border overflow-y-auto animate-slide-in">
        {/* Header */}
        <div className="sticky top-0 bg-background/95 backdrop-blur-sm border-b border-border p-5 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg font-bold text-text-primary">{metricLabel}</h2>
              <p className="text-sm font-body text-text-secondary mt-0.5">Current: <span className="font-semibold text-text-primary">{metricValue}</span></p>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="oee-ai-assist-btn"
                onClick={handleAiAssist}
                disabled={aiLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 transition-all text-xs font-body font-semibold disabled:opacity-50"
              >
                {aiLoading ? <Spinner size={14} className="animate-spin" /> : <Robot size={14} weight="fill" />}
                AI Assist
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-text-secondary hover:text-text-primary transition-colors">
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="space-y-4 animate-pulse">
              {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-surface border border-border rounded-lg" />)}
            </div>
          ) : drilldown ? (
            <>
              {/* Description */}
              <div className="bg-surface border border-border rounded-lg p-4">
                <p className="text-sm font-body text-text-secondary">{drilldown.config?.description}</p>
              </div>

              {/* Line Breakdown Chart */}
              {drilldown.line_breakdown?.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="font-heading text-sm font-semibold text-text-primary mb-3">Breakdown by Production Line</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={drilldown.line_breakdown} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <XAxis type="number" tick={{ fill: '#A3A380', fontSize: 11 }} />
                        <YAxis dataKey="line_id" type="category" width={40} tick={{ fill: '#A3A380', fontSize: 11 }} />
                        <Tooltip content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload;
                          return (
                            <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body">
                              <p className="font-semibold text-text-primary mb-1">{d.name}</p>
                              <p className="text-text-secondary">Avg: <span className="text-primary font-medium">{d.avg}</span></p>
                              <p className="text-text-secondary">Range: {d.min} – {d.max}</p>
                              <p className="text-text-secondary">Alerts: {d.alert_count}</p>
                            </div>
                          );
                        }} />
                        <Bar dataKey="avg" fill="#3D4B32" radius={[0, 4, 4, 0]} barSize={16} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Line detail table */}
                  <div className="mt-3 space-y-1.5">
                    {drilldown.line_breakdown.map(l => (
                      <div key={l.line_id} className="flex items-center justify-between text-xs font-body py-1.5 border-b border-border/50 last:border-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-text-primary w-8">{l.line_id}</span>
                          <span className="text-text-secondary">{l.name}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-text-secondary">{l.asset_type}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            l.criticality === 'High' ? 'bg-red-500/15 text-red-400' :
                            l.criticality === 'Medium' ? 'bg-amber-500/15 text-amber-400' :
                            'bg-emerald-500/15 text-emerald-400'
                          }`}>{l.criticality}</span>
                          <span className="font-mono font-semibold text-text-primary w-16 text-right">{l.avg}</span>
                          {l.alert_count > 0 && <span className="text-amber-400 text-[10px]">{l.alert_count} alerts</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shift Breakdown */}
              {drilldown.shift_breakdown?.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="font-heading text-sm font-semibold text-text-primary mb-3">Breakdown by Shift</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {drilldown.shift_breakdown.map(s => (
                      <div key={s.shift} className="bg-background rounded-lg p-3 text-center">
                        <p className="text-xs font-body font-semibold text-text-primary">{s.shift}</p>
                        <p className="font-heading text-xl font-bold text-primary mt-1">{s.avg}</p>
                        <p className="text-[10px] font-body text-text-secondary">{s.count} data points</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Losses */}
              {drilldown.related_losses?.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="font-heading text-sm font-semibold text-text-primary mb-3">Contributing Loss Categories</h3>
                  <div className="space-y-2">
                    {drilldown.related_losses.map((loss, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-background rounded-lg p-3">
                        <div>
                          <p className="text-sm font-body font-medium text-text-primary">{loss.category}</p>
                          <p className="text-xs font-body text-text-secondary">{loss.loss_minutes} min | ESG: {loss.esg_alignment}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-body font-bold text-red-400">-{loss.loss_pct}%</p>
                          <p className="text-xs font-body text-amber-400">{fmtVnd(loss.vnd_impact)} VND</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Related Initiatives */}
              {drilldown.related_initiatives?.length > 0 && (
                <div className="bg-surface border border-border rounded-lg p-4">
                  <h3 className="font-heading text-sm font-semibold text-text-primary mb-3">Related Improvement Initiatives</h3>
                  <div className="space-y-2">
                    {drilldown.related_initiatives.map((ini, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-background rounded-lg p-3">
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="text-sm font-body font-medium text-text-primary truncate">{ini.init_id}: {ini.title}</p>
                          <div className="flex gap-2 mt-0.5 text-[10px] font-body">
                            <span className={`px-1.5 py-0.5 rounded-full ${
                              ini.status === 'Completed' ? 'bg-emerald-500/15 text-emerald-400' :
                              ini.status === 'In Progress' ? 'bg-amber-500/15 text-amber-400' :
                              'bg-blue-500/15 text-blue-400'
                            }`}>{ini.status}</span>
                            <span className="text-text-secondary">+{ini.oee_points_lifted} OEE pp</span>
                          </div>
                        </div>
                        <p className="text-sm font-body font-bold text-emerald-400 shrink-0">{fmtVnd(ini.vnd_saved_day)} VND/day</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Insight Section */}
              {showAi && (
                <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-5 animate-fade-in" data-testid="oee-ai-insight">
                  <div className="flex items-center gap-2 mb-3">
                    <Robot size={18} className="text-violet-400" weight="fill" />
                    <h3 className="font-heading text-sm font-semibold text-violet-400">AI Insights — {metricLabel}</h3>
                  </div>
                  {aiLoading ? (
                    <div className="flex items-center gap-2 py-8 justify-center text-sm font-body text-text-secondary">
                      <Spinner size={18} className="animate-spin text-violet-400" />
                      Analyzing metric data with Claude Sonnet 4.5...
                    </div>
                  ) : (
                    <div className="prose prose-sm prose-invert max-w-none text-text-secondary font-body
                      [&_h2]:text-text-primary [&_h2]:font-heading [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2
                      [&_h3]:text-text-primary [&_h3]:font-heading [&_h3]:text-xs [&_h3]:font-semibold
                      [&_strong]:text-text-primary [&_ul]:space-y-1 [&_li]:text-xs [&_p]:text-xs [&_p]:leading-relaxed">
                      <ReactMarkdown>{aiInsight || ''}</ReactMarkdown>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm font-body text-text-secondary">No drilldown data available.</p>
          )}
        </div>
      </div>
    </div>
  );
}
